'use client'

import { Button } from '@dafiti/closet-ui'
import { Chevron } from '@dafiti/closet-ui/icons'
import { cn } from '@dafiti/closet-ui/utils'
import { useState } from 'react'
import { FooterLinkSection } from './FooterLinkSection'
import type { GetCommonsFooterResponseData } from '@/queries/footer'
import type { FooterLinkSectionProps } from './FooterLinkSection'
import { useMessages } from '@/i18n/useMessages'

type Props = {
  data: GetCommonsFooterResponseData
  className?: string
}

export const FooterHighlightedLinks = ({ data, className }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const t = useMessages()

  const getItems = (
    items: GetCommonsFooterResponseData['footer']['top_brands']['items'],
  ) => {
    return items.map((item) => ({
      label: item.label ?? '',
      href: item.url ?? '',
      icon: item.image_url ? (
        <img src={item.image_url} aria-hidden="true" />
      ) : undefined,
    }))
  }

  const highlightedFooterLinks: Array<FooterLinkSectionProps> = [
    {
      title: data.footer.top_brands.label,
      icon: <img src={data.footer.top_brands.image_url} aria-hidden="true" />,
      items: getItems(data.footer.top_brands.items),
    },
    {
      title: data.footer.top_categories.label,
      icon: (
        <img src={data.footer.top_categories.image_url} aria-hidden="true" />
      ),
      items: getItems(data.footer.top_categories.items),
    },
  ]

  function scrollToBottom() {
    window.scrollTo({
      left: 0,
      top: document.body.scrollHeight,
      behavior: 'smooth',
    })
  }

  return (
    <>
      <div className="-translate-y-2xs absolute left-1/2 z-[1] -translate-x-1/2">
        <Button
          className="w-giant h-sm hidden border-none !bg-gray-100 lg:flex"
          variant="secondary"
          onClick={() => {
            if (!isOpen) {
              setTimeout(scrollToBottom, 400)
            }

            setIsOpen(!isOpen)
          }}
        >
          {isOpen ? t.footer.seeLess : t.footer.seeMore}

          <Chevron
            className={cn(
              'ml-nano size-[10px] transition-transform duration-300',
              {
                'rotate-180': !isOpen,
              },
            )}
          />
        </Button>
      </div>

      <div
        className={cn(className, 'relative overflow-y-hidden duration-300', {
          'lg:h-0 lg:py-[0px]': !isOpen,
        })}
        style={{
          interpolateSize: 'allow-keywords',
        }}
      >
        <FooterLinkSection.Container>
          {highlightedFooterLinks.map((link) => (
            <li
              key={link.title}
              className="col-span-2 contents w-full lg:block"
            >
              <FooterLinkSection {...link} columns={3} useRouterLink />
            </li>
          ))}
        </FooterLinkSection.Container>
      </div>
    </>
  )
}
